import { useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { RiDownload2Line } from '@remixicon/react'
import type { ArtifactDetail } from 'shared'
import { Button } from '../ui'
import { ReportPrintSheet } from './ReportPrintSheet'

interface DownloadReportPdfProps {
  artifact: ArtifactDetail
  /** What to print; defaults to the version the reader is looking at. */
  content?: string
  workspaceName?: string
}

/**
 * "Save as PDF" through the browser's print dialog: the text stays text, the
 * charts stay vector, and nothing is generated server side. The app is hidden
 * by the print stylesheet while `data-print-report` is on `body`.
 */
export function DownloadReportPdf({ artifact, content, workspaceName }: DownloadReportPdfProps) {
  const { t } = useTranslation()
  const [printing, setPrinting] = useState(false)
  const body = content ?? artifact.version?.content_md ?? ''

  const print = () => {
    const root = document.documentElement
    const wasDark = root.classList.contains('dark')
    const previousTitle = document.title

    // The browser proposes the page title as the file name.
    document.title = `ScaleBrain - ${artifact.title}`
    if (wasDark) root.classList.remove('dark')
    document.body.setAttribute('data-print-report', '')

    const done = () => {
      document.body.removeAttribute('data-print-report')
      if (wasDark) root.classList.add('dark')
      document.title = previousTitle
      window.removeEventListener('afterprint', done)
      setPrinting(false)
    }
    window.addEventListener('afterprint', done)

    // The sheet exists only while printing, so it never doubles what the page
    // itself shows (charts, headings) for anyone reading or testing it.
    flushSync(() => setPrinting(true))
    window.print()
  }

  if (!body.trim()) return null

  return (
    <>
      <Button variant="secondary" onClick={print}>
        <RiDownload2Line className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
        {t('report.pdf.download')}
      </Button>
      {printing && (
        <ReportPrintSheet
          title={artifact.title}
          summary={artifact.summary}
          content={body}
          publishedAt={artifact.published_at}
          versionNumber={artifact.version?.version_number ?? null}
          workspaceName={workspaceName}
        />
      )}
    </>
  )
}
